/**
 * @fileoverview The frame every org Settings page renders in (see
 * lib/orgSettings.js): one reading column, and above a section's own heading a
 * breadcrumb back to the hub.
 *
 * The last crumb is also a menu of the other sections, so an admin moving from
 * Branding to Features does not have to go back through the hub. Hand-rolled
 * like ExportMenu: a Radix menu is not installed and this is five links.
 * Outside-click and Escape close it; so does arriving on another page.
 */

import { useEffect, useRef, useState } from "react";
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import { Icon, PageShell } from "@/components/studio/primitives";
import { ORG_SETTINGS_PATH, ORG_SETTINGS_SECTIONS, settingsSectionFor } from "@/lib/orgSettings";

function SectionSwitcher({ section }) {
  const [open, setOpen] = useState(false);
  const root = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (root.current && !root.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-current="page"
        data-testid="org-settings-switcher"
        className="inline-flex items-center gap-1 rounded-lg px-1.5 py-0.5 font-medium text-[var(--text-primary)] transition duration-150 hover:bg-[var(--studio-overlay)] active:scale-95"
      >
        {section.label}
        <Icon name="chevronDown" size={14} className="text-[var(--text-tertiary)]" />
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Settings sections"
          data-testid="org-settings-switcher-panel"
          className="absolute left-0 top-full z-30 mt-2 w-[240px] rounded-2xl border border-[var(--studio-divider-strong)] bg-[var(--studio-glass)] p-1.5 shadow-[var(--studio-shadow)] backdrop-blur-xl backdrop-saturate-150"
        >
          {ORG_SETTINGS_SECTIONS.map((s) => {
            const current = s.id === section.id;
            return (
              <Link
                key={s.id}
                to={s.path}
                role="menuitem"
                aria-current={current ? "page" : undefined}
                onClick={() => setOpen(false)}
                data-testid={`org-settings-switch-${s.id}`}
                className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 transition-colors hover:bg-[var(--studio-overlay)]"
              >
                <Icon
                  name={s.icon}
                  size={15}
                  className={current ? "text-[var(--accent-yellow-text)]" : "text-[var(--text-tertiary)]"}
                />
                <span
                  className={[
                    "flex-1 font-sans text-[14px]",
                    current ? "font-semibold text-[var(--text-primary)]" : "text-[var(--text-secondary)]",
                  ].join(" ")}
                >
                  {s.label}
                </span>
                {current && <Icon name="check" size={15} className="text-[var(--accent-yellow-text)]" />}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Settings › {section}, where "Settings" goes back to the hub. */
export function SettingsBreadcrumb({ section }) {
  return (
    <nav aria-label="Breadcrumb" data-testid="org-settings-breadcrumb" className="mb-4">
      <ol className="-ml-1.5 flex items-center gap-0.5 font-sans text-[13px]">
        <li>
          <Link
            to={ORG_SETTINGS_PATH}
            data-testid="org-settings-crumb"
            className="inline-flex items-center rounded-lg px-1.5 py-0.5 text-[var(--text-tertiary)] transition-colors duration-150 hover:text-[var(--text-primary)]"
          >
            Settings
          </Link>
        </li>
        <li aria-hidden="true" className="flex items-center">
          <Icon name="chevronRight" size={14} className="text-[var(--text-quaternary)]" />
        </li>
        <li>
          <SectionSwitcher section={section} />
        </li>
      </ol>
    </nav>
  );
}

/**
 * A retired path, sent on to where its page lives now with its query string
 * and hash intact: Stripe's checkout returns to `/org/subscription?checkout=…`,
 * and the Plan page reads that parameter to say how checkout went.
 */
export function MovedTo({ to }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

export default function OrgSettingsLayout() {
  const { pathname } = useLocation();
  const section = settingsSectionFor(pathname);

  return (
    <PageShell data-testid="org-settings-layout">
      {section && <SettingsBreadcrumb section={section} />}
      <Outlet />
    </PageShell>
  );
}
